import type { Node } from '@xyflow/react';
import type { IBaseData } from '@shared/production-editor';

export type FlowNode = Node<IBaseData>;

const GROUP_GAP = 64;

function getNodeSize(node: FlowNode, defaultWidth: number, defaultHeight: number) {
    return {
        width: node.width ?? node.measured?.width ?? defaultWidth,
        height: node.height ?? node.measured?.height ?? defaultHeight,
    };
}

function isProductionLine(node: FlowNode) {
    if (node.type !== 'group' || node.parentId) return false;
    const name = `${node.data.entityLabel ?? ''} ${node.data.label ?? ''}`.toLocaleLowerCase('fr');
    return name.includes('ligne');
}

function isPumpGroup(node: FlowNode) {
    if (node.type !== 'group' || node.parentId) return false;
    const name = `${node.data.entityLabel ?? ''} ${node.data.label ?? ''}`.toLocaleLowerCase('fr');
    return name.includes('pompe') || name.includes('pump');
}

export function layoutGroupContents(nodes: FlowNode[], onlyGroupId?: string): FlowNode[] {
    let layoutedNodes = nodes;
    const groups = nodes.filter(
        (node) => node.type === 'group' && (!onlyGroupId || node.id === onlyGroupId),
    );

    groups.forEach((group) => {
        const children = layoutedNodes.filter((node) => node.parentId === group.id);
        if (children.length === 0) return;

        const usesPumpLayout = isPumpGroup(group);
        const gap = 20;
        const sidePadding = 24;
        const titleSpace = 56;
        const technicalLabelSpace = 44;
        const childSizes = children.map((child) => ({
            id: child.id,
            ...getNodeSize(child, 140, 100),
        }));

        let groupWidth: number;
        let groupHeight: number;
        const positions = new Map<string, { x: number; y: number }>();

        if (usesPumpLayout) {
            const contentWidth = Math.max(...childSizes.map((child) => child.width));
            const contentHeight =
                childSizes.reduce((total, child) => total + child.height, 0) +
                gap * Math.max(0, childSizes.length - 1);
            groupWidth = contentWidth + sidePadding * 2;
            groupHeight = titleSpace + contentHeight + technicalLabelSpace;
            let y = titleSpace;
            childSizes.forEach((child) => {
                positions.set(child.id, { x: (groupWidth - child.width) / 2, y });
                y += child.height + gap;
            });
        } else if (childSizes.length <= 3) {
            const contentWidth =
                childSizes.reduce((total, child) => total + child.width, 0) +
                gap * Math.max(0, childSizes.length - 1);
            const contentHeight = Math.max(...childSizes.map((child) => child.height));
            groupWidth = contentWidth + sidePadding * 2;
            groupHeight = titleSpace + contentHeight + technicalLabelSpace;
            let x = sidePadding;
            childSizes.forEach((child) => {
                positions.set(child.id, {
                    x,
                    y: titleSpace + (contentHeight - child.height) / 2,
                });
                x += child.width + gap;
            });
        } else {
            const columnCount = Math.ceil(Math.sqrt(childSizes.length));
            const rowCount = Math.ceil(childSizes.length / columnCount);
            const columnWidths = Array.from({ length: columnCount }, (_, columnIndex) =>
                Math.max(
                    ...childSizes
                        .filter((_, index) => index % columnCount === columnIndex)
                        .map((child) => child.width),
                ),
            );
            const rowHeights = Array.from({ length: rowCount }, (_, rowIndex) =>
                Math.max(
                    ...childSizes
                        .slice(rowIndex * columnCount, (rowIndex + 1) * columnCount)
                        .map((child) => child.height),
                ),
            );
            groupWidth =
                columnWidths.reduce((total, width) => total + width, 0) +
                gap * (columnCount - 1) +
                sidePadding * 2;
            groupHeight =
                titleSpace +
                rowHeights.reduce((total, height) => total + height, 0) +
                gap * (rowCount - 1) +
                technicalLabelSpace;
            childSizes.forEach((child, index) => {
                const column = index % columnCount;
                const row = Math.floor(index / columnCount);
                const precedingColumns =
                    columnWidths.slice(0, column).reduce((total, width) => total + width, 0) +
                    gap * column;
                const precedingRows =
                    rowHeights.slice(0, row).reduce((total, height) => total + height, 0) +
                    gap * row;
                positions.set(child.id, {
                    x: sidePadding + precedingColumns + (columnWidths[column] - child.width) / 2,
                    y: titleSpace + precedingRows + (rowHeights[row] - child.height) / 2,
                });
            });
        }

        layoutedNodes = layoutedNodes.map((node) => {
            if (node.id === group.id) return { ...node, width: groupWidth, height: groupHeight };
            const position = positions.get(node.id);
            return position ? { ...node, position } : node;
        });
    });

    return layoutedNodes;
}

function alignProductionFlow(nodes: FlowNode[]): FlowNode[] {
    const productionLines = nodes
        .filter(isProductionLine)
        .sort((first, second) =>
            String(first.data.label).localeCompare(String(second.data.label), 'fr', { numeric: true }),
        );
    if (productionLines.length === 0) return nodes;

    const axisNodes = nodes.filter(
        (node) =>
            !node.parentId &&
            (node.data.entityName === 'Departure' ||
                node.data.entityName === 'Return' ||
                isProductionLine(node)),
    );
    const centers = axisNodes
        .map((node) => {
            const { height } = getNodeSize(node, 140, 120);
            return node.position.y + height / 2;
        })
        .sort((first, second) => first - second);
    const middle = Math.floor(centers.length / 2);
    const axisY =
        centers.length % 2 === 0
            ? (centers[middle - 1] + centers[middle]) / 2
            : centers[middle];
    const lineX = [...productionLines]
        .sort((first, second) => first.position.x - second.position.x)[
            Math.floor(productionLines.length / 2)
        ].position.x;
    const totalLineHeight =
        productionLines.reduce(
            (total, line) => total + getNodeSize(line, 250, 180).height,
            0,
        ) +
        GROUP_GAP * Math.max(0, productionLines.length - 1);
    let nextLineY = axisY - totalLineHeight / 2;
    const linePositions = new Map<string, { x: number; y: number }>();
    const pumpGroups = nodes.filter(isPumpGroup);
    const widestProductionLine = Math.max(
        ...productionLines.map((line) => getNodeSize(line, 250, 180).width),
    );
    const singlePumpGroup = pumpGroups.length === 1 ? pumpGroups[0] : undefined;
    const pumpGroupPosition = singlePumpGroup
        ? {
            x: lineX + widestProductionLine + GROUP_GAP,
            y: axisY - getNodeSize(singlePumpGroup, 250, 180).height / 2,
        }
        : undefined;

    productionLines.forEach((line) => {
        const { height } = getNodeSize(line, 250, 180);
        linePositions.set(line.id, { x: lineX, y: nextLineY });
        nextLineY += height + GROUP_GAP;
    });

    return nodes.map((node) => {
        const linePosition = linePositions.get(node.id);
        if (linePosition) return { ...node, position: linePosition };
        if (
            !node.parentId &&
            (node.data.entityName === 'Departure' || node.data.entityName === 'Return')
        ) {
            const { width, height } = getNodeSize(node, 140, 120);
            const x =
                node.data.entityName === 'Return'
                    ? lineX - width - GROUP_GAP
                    : singlePumpGroup && pumpGroupPosition
                      ? pumpGroupPosition.x +
                        getNodeSize(singlePumpGroup, 250, 180).width +
                        GROUP_GAP
                      : lineX + widestProductionLine + GROUP_GAP;
            return { ...node, position: { x, y: axisY - height / 2 } };
        }
        if (singlePumpGroup && pumpGroupPosition && node.id === singlePumpGroup.id) {
            return { ...node, position: pumpGroupPosition };
        }
        return node;
    });
}

export function spaceTopLevelGroups(nodes: FlowNode[]): FlowNode[] {
    const groups = nodes
        .filter((node) => node.type === 'group' && !node.parentId)
        .map((node) => ({
            id: node.id,
            isProductionLine: isProductionLine(node),
            x: node.position.x,
            y: node.position.y,
            ...getNodeSize(node, 250, 180),
        }))
        .sort((first, second) => {
            if (first.isProductionLine !== second.isProductionLine) {
                return first.isProductionLine ? -1 : 1;
            }
            return first.x - second.x || first.y - second.y;
        });
    const placed: typeof groups = [];

    groups.forEach((group) => {
        const next = { ...group };
        let moved = true;
        while (moved) {
            moved = false;
            for (const previous of placed) {
                const overlapsHorizontally =
                    next.x < previous.x + previous.width + GROUP_GAP &&
                    next.x + next.width + GROUP_GAP > previous.x;
                const overlapsVertically =
                    next.y < previous.y + previous.height + GROUP_GAP &&
                    next.y + next.height + GROUP_GAP > previous.y;
                if (!overlapsHorizontally || !overlapsVertically) continue;

                const horizontalDistance = Math.abs(next.x - previous.x);
                const verticalDistance = Math.abs(next.y - previous.y);
                if (horizontalDistance >= verticalDistance) {
                    next.x = previous.x + previous.width + GROUP_GAP;
                } else {
                    next.y = previous.y + previous.height + GROUP_GAP;
                }
                moved = true;
                break;
            }
        }
        placed.push(next);
    });

    const positions = new Map(placed.map((group) => [group.id, { x: group.x, y: group.y }]));
    return nodes.map((node) => {
        const position = positions.get(node.id);
        return position ? { ...node, position } : node;
    });
}

export function formatProductionLayout(nodes: FlowNode[]): FlowNode[] {
    return spaceTopLevelGroups(alignProductionFlow(layoutGroupContents(nodes)));
}
